/* ==========================================================================
   Mentally Fit with Ellin — mf-carousel.js
   Shared by the carousel sections (MF Logos, MF Testimonials, MF Events).

   <mf-carousel data-speed="450">
     ... <ul data-mf-track> slides </ul> ...
     <button name="previous"> <button name="next">
   </mf-carousel>

   The section's CSS sets --mf-carousel-view to the number of slides in view
   and sizes the slides. This script only moves them.

   Optional: data-lead="1" parks the last slide in front of the first one,
   so a section can let it peek in from the left. The script then sets
   --mf-carousel-lead to 1 on the element, and the section's CSS shifts the
   track left by one slide. Without the attribute nothing changes.
   ========================================================================== */

/* mf-carousel: endless one-slide-at-a-time carousel.

   The slide is animated by this script (Web Animations API), not by a CSS
   transition, so it does not depend on the stylesheet.

   How it loops without cloning slides:
   - Next: animate the track left by one slide. When the animation ends,
     move the first slide to the end. The animation leaves no transform
     behind, so the track is back at 0 with the next logo in place.
   - Previous: move the last slide to the front, then animate the track
     from one slide left back to 0.
   The resting position is always 0, so resizing needs no recalculation.
   Needs at least one more slide than the number in view; the sections
   hide the arrows otherwise.

   With a parked slide (data-lead) and only a few slides, one slide is
   needed at both ends during a move: its edge shows on one side when the
   move starts and on the other side when it ends. In that case the slide
   changes ends part-way through, while it is out of sight on both sides,
   and the track is shifted by one slide the other way at the same moment,
   so nothing on screen jumps. */
if (!customElements.get('mf-carousel')) {
  class MfCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-mf-track]');
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
      if (!this.track) return;

      this.animation = null;
      this.pending = null;
      this.swapTimer = null;
      this.lead = 0; /* slides parked in front of the first one in view: 0 or 1 */

      this.onPrev = () => this.move(-1);
      this.onNext = () => this.move(1);
      this.onTouchStart = (event) => {
        this.touchX = event.touches[0].clientX;
        this.touchY = event.touches[0].clientY;
      };
      this.onTouchEnd = (event) => {
        if (this.touchX === undefined) return;
        const dx = event.changedTouches[0].clientX - this.touchX;
        const dy = event.changedTouches[0].clientY - this.touchY;
        this.touchX = undefined;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.move(dx < 0 ? 1 : -1);
      };
      this.onBlockSelect = (event) => {
        const slide = Array.from(this.track.children).find((child) => child.contains(event.target));
        if (!slide) return;
        this.settle();
        while (this.track.children[this.lead] !== slide) this.track.append(this.track.firstElementChild);
        this.refresh();
      };

      this.prevButton?.addEventListener('click', this.onPrev);
      this.nextButton?.addEventListener('click', this.onNext);
      this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.track.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      /* Slides outside the viewport are clipped, so the browser would not
         lazy-load their images until they slide in. Load them all once the
         carousel is near the screen. */
      this.imageObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
          this.imageObserver.disconnect();
        },
        { rootMargin: '600px 0px' }
      );
      this.imageObserver.observe(this);

      this.resizeObserver = new ResizeObserver(() => this.refresh());
      this.resizeObserver.observe(this);
      this.refresh();
    }

    disconnectedCallback() {
      this.settle();
      this.prevButton?.removeEventListener('click', this.onPrev);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('touchstart', this.onTouchStart);
      this.track?.removeEventListener('touchend', this.onTouchEnd);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.imageObserver?.disconnect();
      this.resizeObserver?.disconnect();
    }

    /* Slides in view, from the CSS variable the section sets per breakpoint. */
    get view() {
      return parseInt(getComputedStyle(this).getPropertyValue('--mf-carousel-view'), 10) || 1;
    }

    /* Distance of one move: one slide plus the gap. */
    get step() {
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return this.track.firstElementChild.getBoundingClientRect().width + gap;
    }

    /* Slide time in milliseconds, from the data-speed attribute. */
    get speed() {
      return parseInt(this.dataset.speed, 10) || 450;
    }

    move(direction) {
      const total = this.track.children.length;
      if (total <= this.view) return;
      this.settle(); /* a click mid-slide finishes the current one first */

      const step = this.step;
      /* True when the parked slide is needed at both ends during one move. */
      const tight = this.lead > 0 && total < this.view + 3;
      let from = 0;
      let to = -step;

      if (direction > 0) {
        this.pending = 'next';
      } else if (tight) {
        this.pending = 'previous-late';
        to = step;
      } else {
        this.track.prepend(this.track.lastElementChild);
        this.pending = 'previous';
        from = -step;
        to = 0;
      }

      this.refresh(true);

      this.animation = this.track.animate(
        [
          { transform: `translate3d(${from}px, 0, 0)` },
          { transform: `translate3d(${to}px, 0, 0)` },
        ],
        { duration: this.speed, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
      );
      this.animation.onfinish = () => this.settle();

      /* With this easing the track is exactly half a slide along after 35%
         of the time. */
      if (tight) this.swapTimer = setTimeout(() => this.swap(), this.speed * 0.35);
    }

    /* Part-way through a tight move: send the out-of-sight slide to the
       other end and shift the track back by one slide to make up for it. */
    swap() {
      this.swapTimer = null;
      const step = this.step;
      if (this.pending === 'next') {
        this.track.append(this.track.firstElementChild);
        this.track.style.translate = `${step}px`;
      } else if (this.pending === 'previous-late') {
        this.track.prepend(this.track.lastElementChild);
        this.track.style.translate = `${-step}px`;
      } else {
        return;
      }
      this.pending = 'placed';
    }

    /* Finish the current move: drop the animation and put the slide that
       has to change ends in its place, if that has not happened yet. */
    settle() {
      if (!this.pending) return;
      clearTimeout(this.swapTimer);
      this.swapTimer = null;
      if (this.animation) {
        this.animation.onfinish = null;
        this.animation.cancel();
        this.animation = null;
      }
      if (this.pending === 'next') this.track.append(this.track.firstElementChild);
      if (this.pending === 'previous-late') this.track.prepend(this.track.lastElementChild);
      this.track.style.translate = '';
      this.pending = null;
      this.refresh();
    }

    /* Park or release the leading slide. Asked for with data-lead, and only
       done when there are more slides than fit in view. */
    syncLead() {
      const wanted = parseInt(this.dataset.lead, 10) > 0 && this.track.children.length > this.view ? 1 : 0;
      if (wanted === this.lead) return;
      if (wanted) this.track.prepend(this.track.lastElementChild);
      else this.track.append(this.track.firstElementChild);
      this.lead = wanted;
      this.style.setProperty('--mf-carousel-lead', wanted);
    }

    /* Slides out of view cannot take keyboard focus. While sliding, the
       incoming slide is also kept reachable. */
    refresh(moving = false) {
      if (!this.pending) this.syncLead();
      const first = moving ? 0 : this.lead;
      const last = this.lead + this.view - 1 + (moving ? 1 : 0);
      Array.from(this.track.children).forEach((slide, index) => {
        slide.toggleAttribute('inert', index < first || index > last);
      });
    }
  }

  customElements.define('mf-carousel', MfCarousel);
}
